import React, { useState } from "react";
import { useMask } from "@react-input/mask";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import Box from "./Box";
import CustomSingleSelect, { CustomSingleSelectModel } from "./CustomSingleSelect";
import { Helpers } from "../helpers/Helpers";
import { storeActivityIndicator, storeFastDriver } from "../redux/reducers/authSlice";
import { ConstantService } from "../services/ConstantService";
import { DriverService } from "../services/DriverService";
import { CompanyService } from "../services/CompanyService";
import { AddDriverRequest } from "../models/request/driver/AddDriverRequest";
import { AddDriverResponse, AddDriverResponseData } from "../models/response/driver/AddDriverResponse";

export interface FastDriverProps {
    /** Bir önceki adımda tanımlanan firma; sürücünün bağlı olduğu firma olarak kilitli gelir. */
    /** Bir önceki adımda tanımlanan firma; kilitli gelir. Boşsa ("Firma tanımlı") firma bu panelde tanımlı firmalardan seçilir. */
    relatedCompanyId?: string
    relatedCompanyName?: string
    /** firma: sürücünün bağlandığı firma (önceki adımdan gelen ya da bu panelde seçilen); araç adımına aktarılır. */
    onSubmit: (response: AddDriverResponseData, firma: { companyId: string, name: string }) => void
}

/**
 * Hızlı Sürücü tanımlama paneli. Sürücü tanımlama ekranının zorunlu alanlarını taşır;
 * kalan alanlar sürücü kartından tamamlanır.
 */
const FastDriver = ({ relatedCompanyId, relatedCompanyName, onSubmit }: FastDriverProps) => {
    const dispatch = useDispatch();

    // "Firma tanımlı" ile gelindiyse firma burada seçilir (sürücü ekranındaki gibi Tedarikçi işaretli firmalar).
    const firmaSecilir = !relatedCompanyId;
    const [secilenFirma, setSecilenFirma] = useState<CustomSingleSelectModel | undefined>(undefined);
    const firmaId = firmaSecilir ? secilenFirma?.value : relatedCompanyId;
    const firmaAdi = firmaSecilir ? secilenFirma?.label : relatedCompanyName;

    const [name, setName] = useState('');
    const [surname, setSurname] = useState('');
    const [phone1, setPhone1] = useState('');
    const [email, setEmail] = useState('');
    const [selectedCitizenship, setSelectedCitizenship] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [tckn, setTckn] = useState('');

    // Telefon maskesi surucu ekraniyla ayni bicimde.
    const phone1Ref = useMask({ mask: '(___) ___-__-__', replacement: { _: /\d/ } });

    /** Uyruk "TC" ise kimlik numarası görünür ve zorunludur. */
    const isTc = selectedCitizenship?.additionalValue1 == 'TC';


    const kapat = () => dispatch(storeFastDriver(undefined));

    const saveDriver = async () => {
        try {
            if (Helpers.isEmpty(name))
                toast.error('Adı alanını doldurunuz!')
            else if (Helpers.isEmpty(surname))
                toast.error('Soyadı alanını doldurunuz!')
            else if (!firmaId)
                toast.error('Bağlı Olduğu Firma alanını doldurunuz!')
            else if (Helpers.isEmpty(phone1))
                toast.error('Telefon 1 alanını doldurunuz!')
            else if (phone1.length < 15)
                toast.error('Lütfen geçerli bir telefon numarası giriniz!')
            else if (!Helpers.isEmpty(email) && !Helpers.validateEmail(email))
                toast.error('Lütfen geçerli bir email adresi giriniz!')
            else if (!selectedCitizenship)
                toast.error('Uyruğu alanını doldurunuz!')
            // TC Kimlik No zorunlu değil ve biçimi denetlenmez (08.10.2026, Burak): sürücü ekranında olduğu gibi
            // rakam girilen değer olduğu gibi kaydedilir (ör. TCKN'si bilinmeyen sürücüde 11111111111).
            else {
                dispatch(storeActivityIndicator(true));

                var obj: AddDriverRequest = {
                    driverId: undefined,
                    name: name,
                    surname: surname,
                    relatedCompanyId: firmaId,
                    phone1: phone1,
                    email: email,
                    citizenshipId: selectedCitizenship.value,
                    // Uyruk TC degilse kimlik numarasi gonderilmez.
                    tckn: isTc && !Helpers.isEmpty(tckn) ? tckn : undefined,
                    isCustomActive: 1,
                    periodicalDocuments: [],
                    fileArchives: []
                };

                var response: AddDriverResponse = await DriverService.addDriver(obj);

                if (!response.isSuccess) {
                    toast.error(response.message);
                    dispatch(storeActivityIndicator(false));
                    return;
                }

                dispatch(storeActivityIndicator(false));
                kapat();
                onSubmit(response.data, { companyId: firmaId!, name: firmaAdi ?? '' });
            }
        } catch (error: any) {
            toast.error(error);
            dispatch(storeActivityIndicator(false));
        }
    }

    return (
        <div style={{ position: 'fixed', top: 0, right: 0, background: 'rgba(0, 0, 0, 0.66)', zIndex: 999, width: '100%', height: '100%' }}>
            <Box width="100%" height="100%" flexDirection="row">
                <Box onClick={kapat} flexGrow={1}>&nbsp;</Box>

                <Box width="400px" height="100%" alignSelf="flex-end" bg='white'>
                    <Box className="mb-10" flexDirection="row" pl="15px" pr="15px" pt="15px">
                        <Box flexGrow={1} justifyContent="center">
                            <h4 className="m-0 text-black">Hızlı Sürücü İşlemleri</h4>
                        </Box>
                        <Box justifyContent="center">
                            <i onClick={kapat} style={{ fontSize: 18 }} className="actionIcon text-muted fa fa-times" />
                        </Box>
                    </Box>

                    <Box overflowY="auto" pl="15px" pr="15px">
                        <div>
                            <label className="form-label required">Adı</label>
                            <input style={{ border: '1px solid #F1F1F4' }} value={name} onChange={(e) => setName(e.target.value)} type="text" className="form-control form-control-solid" />
                        </div>

                        <div className="mt-5">
                            <label className="form-label required">Soyadı</label>
                            <input style={{ border: '1px solid #F1F1F4' }} value={surname} onChange={(e) => setSurname(e.target.value)} type="text" className="form-control form-control-solid" />
                        </div>

                        {/* Önceki adımda tanımlanan firma kilitli; "Firma tanımlı" ile gelindiyse tanımlı firmalardan seçilir. */}
                        <div className="mt-5">
                            {
                                firmaSecilir
                                    ? <CustomSingleSelect
                                        labelText="Bağlı Olduğu Firma"
                                        isLabelRequired
                                        selectedValue={secilenFirma}
                                        onChange={setSecilenFirma}
                                        onMenuOpen={() => CompanyService.getCompanyList({ isSupplier: true })}
                                        labelName='name'
                                        valueName='companyId'
                                    />
                                    : <React.Fragment>
                                        <label className="form-label required">Bağlı Olduğu Firma</label>
                                        <input disabled style={{ border: '1px solid #F1F1F4' }} value={relatedCompanyName} type="text" className="form-control form-control-solid" />
                                    </React.Fragment>
                            }
                        </div>

                        <div className="mt-5">
                            <label className="form-label required">Telefon 1</label>
                            <input ref={phone1Ref} style={{ border: '1px solid #F1F1F4' }} value={phone1} onChange={(e) => setPhone1(e.target.value)} className="form-control form-control-solid" />
                        </div>

                        <div className="mt-5">
                            <label className="form-label">E-Posta</label>
                            <input style={{ border: '1px solid #F1F1F4' }} value={email} onChange={(e) => setEmail(e.target.value)} type="text" className="form-control form-control-solid" />
                        </div>

                        <div className="mt-5">
                            <label className="form-label required">Uyruğu</label>
                            <CustomSingleSelect
                                menuPortal
                                selectedValue={selectedCitizenship}
                                onChange={(selectedItem) => {
                                    setSelectedCitizenship(selectedItem);

                                    // TC vatandasi degilse girilmis kimlik numarasi temizlenir.
                                    if (selectedItem?.additionalValue1 != 'TC')
                                        setTckn('');
                                }}
                                onMenuOpen={() => ConstantService.getConstantList('CITIZENSHIP')}
                                labelName='value'
                                valueName='constantId'
                                additionalValue1Name='additionalValue1'
                            />
                        </div>

                        {
                            isTc && (
                                <div className="mt-5">
                                    <label className="form-label">TC Kimlik No</label>
                                    <input maxLength={11} style={{ border: '1px solid #F1F1F4' }} value={tckn} onChange={(e) => setTckn(e.target.value.replace(/\D/g, ''))} type="text" className="form-control form-control-solid" />
                                    <div className='form-text'>TC Kimlik No bilinmiyorsa 11111111111 yazılabilir.</div>
                                </div>
                            )
                        }
                    </Box>

                    <Box flexDirection="row" justifyContent="flex-end" p="15px">
                        <Box mr="15px">
                            <button className="btn btn-sm btn-light btn-active-light-primary" onClick={kapat}>Geri</button>
                        </Box>
                        <Box>
                            <button className="btn btn-primary btn-sm" onClick={saveDriver}>Kaydet</button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </div>
    )
}

export default FastDriver;
